import { useState } from "react";
import { useContent } from "@/context/ContentContext";
import { useAuth } from "@/context/AuthContext";
import { STRINGS } from "@/content/strings";

// Renders a centralized, editable UI string. In Super Admin edit mode, clicking
// the text lets it be reworded in place; the change saves everywhere immediately.
export function Txt({ id, screen = "app", children, as: Tag = "span", className = "" }) {
  const { get, editMode, updateString, resetString } = useContent();
  const auth = useAuth();
  const me = auth?.me;
  const current = get(id, typeof children === "string" ? children : "");
  const canEdit = editMode && me?.user?.is_super_admin;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const isOverridden = current !== (STRINGS[id] ?? (typeof children === "string" ? children : ""));

  const save = () => {
    if (val !== current) updateString(id, val, screen, current);
    setEditing(false);
  };

  if (canEdit && editing) {
    return (
      <span className="inline-flex items-center gap-1 align-middle" data-live-skip>
        <input
          autoFocus
          data-testid={`edit-input-${id}`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          className="border-2 border-maroon rounded px-1.5 py-0.5 bg-white text-inherit font-inherit min-w-[100px]"
        />
        {isOverridden && (
          <button
            data-testid={`reset-${id}`}
            onMouseDown={(e) => { e.preventDefault(); resetString(id); setEditing(false); }}
            title="Reset to default wording"
            className="text-[12px] text-ink/50 hover:text-maroon"
          >↺</button>
        )}
      </span>
    );
  }

  if (canEdit) {
    return (
      <Tag
        data-testid={`editable-${id}`}
        data-live-skip
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setVal(current); setEditing(true); }}
        title="Click to edit this wording"
        className={`${className} outline-dashed outline-1 outline-maroon/50 rounded cursor-text hover:bg-gold/25`}
      >
        {current}
      </Tag>
    );
  }

  return <Tag className={className}>{current}</Tag>;
}
